/* eslint-disable react/function-component-definition */
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Box, Grid, Select, MenuItem, TextField, Autocomplete, Radio, RadioGroup, FormControlLabel } from "@mui/material";
import dayjs from "dayjs";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import LoadingScreen from "layouts/loading/loadingscreen";
import useProfitLossTableData, { formatNumber } from "./profitLossTableData";
import Swal from "sweetalert2";
import api from "api/api";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";

// ✅ 엑셀 생성용
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";

export default function ProfitLossTableTab() {
  const today = dayjs();
  const [year, setYear] = useState(today.year());
  const [month, setMonth] = useState("ALL");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [accountInput, setAccountInput] = useState("");
  const [radioFilter, setRadioFilter] = useState("전체");
  const didSetDefaultAccountRef = useRef(false);
  const tableBoxRef = useRef(null);
  const noteMeasureCanvasRef = useRef(null);
  const theadRef = useRef(null);
  const [secondRowTop, setSecondRowTop] = useState(0);

  // ✅ 조회된 값 + 변경 감지 버전
  const [editRows, setEditRows] = useState([]);
  const [lockedNoteColumnWidth, setLockedNoteColumnWidth] = useState(null);
  // ✅ 인건비 우클릭 임시 잠금해제 월 목록
  const [unlockedPersonCostMonths, setUnlockedPersonCostMonths] = useState(new Set());
  // ✅ 우클릭 컨텍스트 메뉴 상태
  const [ctxMenu, setCtxMenu] = useState({ open: false, mouseX: 0, mouseY: 0, month: null });
  // ✅ 인건비 등록
  const [personCostUploading, setPersonCostUploading] = useState(false);
  const personCostFileInputRef = useRef(null);

  // ✅ 실제 조회에 사용할 month (전체면 빈값으로 넘김)
  const queryMonth = useMemo(() => {
    return String(month ?? "") === "ALL" ? "" : String(month ?? "");
  }, [month]);

  // ✅ month까지 전달되도록 수정
  const { profitLossTableRows, accountList, loading, fetchProfitLossTableList, savePersonCostExcel } =
    useProfitLossTableData(year, queryMonth, selectedAccountId);

  const accountTypeById = useMemo(() => {
    const map = new Map();
    (profitLossTableRows || []).forEach((r) => {
      if (r?.account_id) map.set(String(r.account_id), r?.account_type);
    });
    return map;
  }, [profitLossTableRows]);

  const accountOptions = useMemo(() => {
    const allOption = { account_id: "ALL", account_name: "전체", account_type: "" };

    const normalized = (accountList || []).map((a) => ({
      account_id: a.account_id,
      account_name: a.account_name,
      account_type: accountTypeById.get(String(a.account_id)) ?? a.account_type ?? "",
    }));

    return [allOption, ...normalized];
  }, [accountList, accountTypeById]);

  const DOSOMAE_ACCOUNT_IDS = new Set([
    "20260126093618",
    "20260126093730",
    "20260126093808",
    "20260127025350",
    "20260127025437",
    "20260127025657",
  ]);

  const filteredAccountOptions = useMemo(() => {
    if (radioFilter === "전체") return accountOptions;
    if (radioFilter === "도소매") {
      return accountOptions.filter(
        (a) => a.account_id === "ALL" || DOSOMAE_ACCOUNT_IDS.has(String(a.account_id))
      );
    }
    // 요양원/산업체: 해당 타입이면서 도소매 업장은 제외
    return accountOptions.filter(
      (a) =>
        a.account_id === "ALL" ||
        (String(a.account_type ?? "").trim() === radioFilter &&
          !DOSOMAE_ACCOUNT_IDS.has(String(a.account_id)))
    );
  }, [accountOptions, radioFilter]);

  const handleRadioChange = useCallback((e) => {
    setRadioFilter(e.target.value);
    setSelectedAccountId("ALL");
    setAccountInput("");
  }, []);

  // 라디오 필터 활성화 시 해당 account_ids 목록 (백엔드 집계용)
  const filterAccountIds = useMemo(() => {
    if (radioFilter === "전체" || selectedAccountId !== "ALL") return "";
    return filteredAccountOptions
      .filter((a) => a.account_id !== "ALL")
      .map((a) => a.account_id)
      .join(",");
  }, [radioFilter, selectedAccountId, filteredAccountOptions]);

  // ✅ 실제 조회에 사용할 account_id (전체면 빈값으로 넘김)
  const queryAccountId = useMemo(() => {
    return selectedAccountId === "ALL" ? "" : selectedAccountId;
  }, [selectedAccountId]);

  // ✅ 데이터 조회 (전체 포함, 월 포함)
  useEffect(() => {
    if (selectedAccountId) {
      fetchProfitLossTableList(queryAccountId, queryMonth, year, filterAccountIds);
    }
  }, [year, queryMonth, queryAccountId, selectedAccountId, fetchProfitLossTableList, filterAccountIds]);

  // ✅ 거래처·연도·월 변경 시 인건비 임시 잠금해제 초기화
  useEffect(() => {
    setUnlockedPersonCostMonths(new Set());
  }, [selectedAccountId, year, queryMonth]);

  // ✅ 두 번째 헤더 행의 sticky top 오프셋 측정
  useEffect(() => {
    const measure = () => {
      if (theadRef.current?.rows?.[0]) {
        setSecondRowTop(theadRef.current.rows[0].offsetHeight);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [editRows]);

  // ✅ 데이터 원본 저장
  useEffect(() => {
    if (profitLossTableRows.length > 0) {
      const cloned = profitLossTableRows.map((row) => ({
        ...row,
        _original: { ...row },
      }));
      setEditRows(cloned);
    } else {
      setEditRows([]);
    }
    setLockedNoteColumnWidth(null);
  }, [profitLossTableRows]);

  // ✅ 계정 자동 선택
  useEffect(() => {
    if (selectedAccountId) {
      didSetDefaultAccountRef.current = true;
    }
  }, [selectedAccountId]);

  useEffect(() => {
    if (didSetDefaultAccountRef.current) return;
    if (accountList.length > 0 && !selectedAccountId) {
      setSelectedAccountId("ALL");
      didSetDefaultAccountRef.current = true;
    }
  }, [accountList, selectedAccountId]);

  // ✅ Autocomplete에서 선택된 객체 (전체 옵션 포함)
  const selectedAccount = useMemo(() => {
    if (!selectedAccountId) return null;
    return (
      (accountOptions || []).find((a) => String(a.account_id) === String(selectedAccountId)) || null
    );
  }, [accountOptions, selectedAccountId]);

  // ✅ account_type 이 "학교" 인 경우에만 반환금(=return_cost) 컬럼 표시
  // "전체"는 여러 유형이 섞여 있으므로, 반환금이 실제로 매출소계에 반영된(0이 아닌) 달이
  // 하나라도 있으면 표시한다 — 안 그러면 반환금이 매출소계에는 빠져 있는데 화면엔 안 보여서
  // 생계비~보전 합계가 매출소계와 어긋나 보인다.
  const showReturnCost = useMemo(() => {
    if (selectedAccountId === "ALL") {
      return (editRows || []).some((r) => Number(r?.return_cost) !== 0);
    }
    const t = String(selectedAccount?.account_type ?? "").trim();
    return t === "학교";
  }, [selectedAccountId, selectedAccount, editRows]);

  const selectAccountByInput = useCallback(() => {
    const q = String(accountInput || "").trim();
    if (!q) return;

    const list = accountOptions || [];
    const qLower = q.toLowerCase();

    const exact = list.find((a) => String(a?.account_name || "").toLowerCase() === qLower);
    const partial =
      exact ||
      list.find((a) =>
        String(a?.account_name || "")
          .toLowerCase()
          .includes(qLower)
      );

    if (partial) {
      setSelectedAccountId(partial.account_id);
      setAccountInput(partial.account_name || q);
    }
  }, [accountInput, accountOptions]);

  // ✅ 숫자 입력 가능한 항목
  const editableNumberFields = [
    "food_process",
    "dishwasher",
    "cesco",
    "water_puri",
    "utility_bills",
    "duty_secure",
    "person_cost",
    "event_cost",
    "not_budget_cost",
    "etc_indirect_cost",
    "return_cost",
    "payback_price",
  ];

  // ✅ 텍스트 입력 가능한 항목
  const editableTextFields = ["utility_bills_note"];
  const noteField = "utility_bills_note";

  // ✅ 숨길 컬럼
  const hiddenCols = ["주간일반", "주간직원"];

  // ✅ 특정 거래처들(주간일반/주간직원 컬럼까지 보여줄 거래처)
  const HANGYEOL_ACCOUNT_IDS = new Set([
    "20250819193455",
    "20250819193504",
    "20250819193603",
    "20250919162439",
    "20250819193620",
    "20250819193632",
    "20250819193622",
    "20250819193615",
    "ALL",
  ]);

  const isHangyeol = HANGYEOL_ACCOUNT_IDS.has(String(selectedAccountId));

  // ✅ 특정 거래처에서만 추가 컬럼 표시
  const SPECIAL_ACCOUNT_ID = "20250819193630";
  const isSpecialSales2 = String(selectedAccountId) === SPECIAL_ACCOUNT_ID;

  // ✅ 화면 헤더 구조 (특정 거래처는 매출에 (2) 컬럼 3개 추가)
  const headers = useMemo(() => {
    const salesColsBase = [
      "생계비",
      "일반식대",
      "직원식대",
      "주간일반",
      "주간직원",
      "보전",
      "반환금",
      "매출소계(판장금제외)",
    ];

    const salesCols = isSpecialSales2
      ? [
        "생계비",
        "일반식대",
        "직원식대",
        "생계비(2)",
        "일반식대(2)",
        "직원식대(2)",
        "주간일반",
        "주간직원",
        "보전",
        "반환금",
        "매출소계(판장금제외)",
      ]
      : salesColsBase;

    return [
      { group: "인원", cols: ["생계인원", "일반인원", "인원합계"] },
      { group: "매출", cols: salesCols },
      {
        group: "매입",
        cols: [
          "식자재",
          "기타경비",
          "음식물처리",
          "식기세척기",
          "세스코방제",
          "정수기",
          "이벤트",
          "예산미발행",
          "초기투자비용",
          "매입소계",
        ],
      },
      { group: "인건", cols: ["인건비정보", "파출비", "인건소계"] },
      { group: "간접", cols: ["수도광열비", "비고", "세금정보", "기타간접비", "간접소계"] },
      { group: "", cols: ["영업이익", "판장금", "총 영업이익"] },
    ];
  }, [isSpecialSales2]);

  // ✅ 컬럼 → DB 필드 매핑
  const fieldMap = {
    생계인원: { value: "living_estimate", ratio: "living_estimate_ratio" },
    일반인원: { value: "basic_estimate", ratio: "basic_estimate_ratio" },
    인원합계: { value: "estimate_total", ratio: "estimate_total_ratio" },
    생계비: { value: "living_cost", ratio: "living_ratio" },
    일반식대: { value: "basic_cost", ratio: "basic_ratio" },
    직원식대: { value: "employ_cost", ratio: "employ_ratio" },
    "생계비(2)": { value: "living_cost2", ratio: "living_ratio2" },
    "일반식대(2)": { value: "basic_cost2", ratio: "basic_ratio2" },
    "직원식대(2)": { value: "employ_cost2", ratio: "employ_ratio2" },
    주간일반: { value: "daycare_cost", ratio: "daycare_ratio" },
    주간직원: { value: "daycare_emp_cost", ratio: "daycare_emp_ratio" },
    보전: { value: "integrity_cost", ratio: "integrity_ratio" },
    반환금: { value: "return_cost", ratio: "return_ratio" },
    판장금: { value: "payback_price", ratio: "payback_ratio" },
    "매출소계(판장금제외)": {
      value: "sales_total",
      ratio: "sales_total_ratio",
    },
    식자재: { value: "food_cost", ratio: "food_ratio" },
    음식물처리: { value: "food_process", ratio: "food_trash_ratio" },
    식기세척기: { value: "dishwasher", ratio: "dishwasher_ratio" },
    세스코방제: { value: "cesco", ratio: "cesco_ratio" },
    정수기: { value: "water_puri", ratio: "water_ratio" },
    기타경비: { value: "etc_cost", ratio: "etc_ratio" },
    이벤트: { value: "event_cost", ratio: "event_ratio" },
    예산미발행: { value: "not_budget_cost", ratio: "not_budget_ratio" },
    초기투자비용: { value: "upfront_cost", ratio: "upfront_cost_ratio" },
    매입소계: { value: "purchase_total", ratio: "purchase_total_ratio" },
    인건비정보: { value: "person_cost", ratio: "person_ratio" },
    파출비: { value: "dispatch_cost", ratio: "dispatch_ratio" },
    인건소계: { value: "person_total", ratio: "person_total_ratio" },
    수도광열비: { value: "utility_bills", ratio: "utility_ratio" },
    비고: { value: "utility_bills_note" },
    세금정보: { value: "duty_secure", ratio: "duty_secure_ratio" },
    기타간접비: { value: "etc_indirect_cost", ratio: "etc_indirect_ratio" },
    간접소계: { value: "indirect_total", ratio: "indirect_total_ratio" },
    영업이익: { value: "business_profit", ratio: "business_profit_ratio" },
    "총 영업이익": { value: "total_business_profit", ratio: "total_business_profit_ratio" },
  };

  // 화면과 엑셀에 표시할 금액 값을 가져오는 함수
  const getDisplayValue = (row, field) => {
    return row?.[field];
  };

  // 그룹별 개별 항목 비율(4자리 정밀도)과, 그 항목들의 합이 맞아야 할 소계 비율 필드.
  // payback_ratio(판장금)는 매출소계에 포함되지 않으므로 매출 그룹에서 제외한다.
  // "전체"는 백엔드에서 강남의 living_cost2/basic_cost2/employ_cost2를 이미
  // living_cost/basic_cost/employ_cost에 합산해서 내려주므로, living_ratio2 등을
  // 여기서 또 더하면 그 만큼 이중으로 잡혀 100%를 넘어간다 — ALL일 때는 제외한다.
  // 매입/인건/간접 소계 비율도 백엔드에서 2자리(ROUND(...,2))로 저장하도록 맞춰서,
  // 모든 그룹을 0.01% 단위 최대잔여법으로 통일한다.
  const RATIO_GROUPS = [
    { items: ["living_estimate_ratio", "basic_estimate_ratio"], target: "estimate_total_ratio", precision: 2 },
    {
      items: [
        ...(selectedAccountId === "ALL" ? [] : ["living_ratio2", "basic_ratio2", "employ_ratio2"]),
        "living_ratio", "basic_ratio", "employ_ratio",
        "daycare_ratio", "daycare_emp_ratio", "integrity_ratio", "return_ratio",
      ],
      target: "sales_total_ratio",
      precision: 2,
    },
    {
      items: [
        "food_trash_ratio", "dishwasher_ratio", "cesco_ratio", "water_ratio",
        "food_ratio", "etc_ratio", "event_ratio", "not_budget_ratio", "upfront_cost_ratio",
      ],
      target: "purchase_total_ratio",
      precision: 2,
    },
    { items: ["person_ratio", "dispatch_ratio"], target: "person_total_ratio", precision: 2 },
    { items: ["utility_ratio", "duty_secure_ratio", "etc_indirect_ratio"], target: "indirect_total_ratio", precision: 2 },
  ];
  const RATIO_GROUP_BY_ITEM = {};
  RATIO_GROUPS.forEach((g) => g.items.forEach((k) => { RATIO_GROUP_BY_ITEM[k] = g; }));

  // 최대잔여법: 그룹 내 항목들을 정해진 자리수(precision, 기본 1자리)로 버림(내림)한 뒤,
  // 원래 값에서 버려진 나머지가 큰 항목부터 최소단위씩 얹어서 표시값의 합이 정확히
  // 소계 비율과 같아지도록 보정한다(DB 원본값은 건드리지 않음).
  const largestRemainderAdjust = (row, group) => {
    const precision = group.precision || 1;
    const step = Math.pow(10, precision);
    const target = Math.round((Number(row?.[group.target]) || 0) * step) / step;
    const items = group.items.map((key) => {
      const raw = Number(row?.[key]) || 0;
      const base = Math.floor(raw * step) / step;
      return { key, base, remainder: raw - base };
    });
    const baseSum = Math.round(items.reduce((s, it) => s + it.base, 0) * step) / step;
    let deficit = Math.round((target - baseSum) * step);
    const order = [...items].sort((a, b) => b.remainder - a.remainder);
    const bump = new Set();
    for (let i = 0; i < order.length && deficit > 0; i++, deficit--) {
      bump.add(order[i].key);
    }
    const out = {};
    items.forEach((it) => {
      out[it.key] = Math.round((it.base + (bump.has(it.key) ? 1 / step : 0)) * step) / step;
    });
    return out;
  };

  // 화면과 엑셀에 표시할 비율 값을 가져오는 함수.
  // 소계에 포함되는 개별 항목은 최대잔여법으로 보정된 값을, 그 외(소계 자체 등)는 원본값을 그대로 반환한다.
  const getDisplayRatio = (row, ratioField) => {
    const group = RATIO_GROUP_BY_ITEM[ratioField];
    if (group && row) {
      return largestRemainderAdjust(row, group)[ratioField];
    }
    return row?.[ratioField];
  };

  // 개별 항목 비율은 DB에 소수점 4자리 정밀도로 저장돼 있다 — 화면에는 2자리로 "표시만" 반올림한다.
  const formatRatio = (v) => {
    if (v == null || v === "") return "";
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : "";
  };

  const formatRatioCell = (row, ratioField) => {
    if (!ratioField) return "-";
    const value = getDisplayRatio(row, ratioField);
    return value ? `${formatRatio(value)}%` : "-";
  };

  const filteredHeaders = headers
    .map((h) => {
      let cols = h.cols;

      if (!isHangyeol) {
        cols = cols.filter((col) => !hiddenCols.includes(col));
      }

      if (!showReturnCost) {
        cols = cols.filter((col) => col !== "반환금");
      }

      return { ...h, cols };
    })
    .filter((h) => h.cols.length > 0);

  // 생계비/일반식대(기본 컬럼)와 생계비(2)/일반식대(2)(강남의 인천강남 병합분)가 모두 없는
  // 월은 합계 계산 대상에서 제외한다. (2026년 5월분부터 강남은 자체 금액까지 전부
  // living_cost2/basic_cost2로만 저장되어 living_cost/basic_cost가 0이 되므로, (2) 필드도 함께 확인해야
  // 그 달이 합계에서 누락되지 않는다.)
  const hasBaseSalesCost = (row) => {
    const hasAmount = (value) => {
      if (value === null || value === undefined || String(value).trim() === "") return false;
      const amount = Number(String(value).replace(/,/g, ""));
      return Number.isFinite(amount) && amount !== 0;
    };

    return (
      hasAmount(row?.living_cost) || hasAmount(row?.basic_cost) ||
      hasAmount(row?.living_cost2) || hasAmount(row?.basic_cost2)
    );
  };

  const totalRow = useMemo(() => {
    if (!editRows || editRows.length === 0) return null;
    const rowsForTotal = editRows.filter(hasBaseSalesCost);
    const result = {};

    // 값 필드 합산 (비율 필드 제외)
    const allValueFields = Object.values(fieldMap)
      .filter((f) => f?.value && f.value !== "utility_bills_note")
      .map((f) => f.value);
    allValueFields.forEach((field) => {
      let sum = 0;
      let hasAny = false;
      rowsForTotal.forEach((row) => {
        const n = Number(row[field]);
        if (Number.isFinite(n)) { sum += n; hasAny = true; }
      });
      result[field] = hasAny ? sum : null;
    });

    // 매출소계(sales_total)는 재계산하지 않고, 각 월의 DB 저장값(판장금 제외 이미 반영됨)을 그대로 합산한
    // result.sales_total(위 allValueFields 루프에서 계산됨)을 사용한다.
    // 구성 항목으로 다시 계산하면 daycare 조건부 포함 등 프로시저 로직과 미세하게 어긋나
    // 개별 월 행과 합계 행의 비율이 달라지는 문제가 있었다.

    // upfront_cost 합계 보정:
    // DB upfront_cost는 잔존가치(잔액)이므로 단순 합산 불가.
    // 1순위: 백엔드 upfront_monthly_deprec 합산
    // 2순위(개별 거래처): 연속 월 잔존가치 차이로 월 감가액 역산
    // 누적: 계약 시작 연도가 조회 연도 이전이면 이전 연도 감가액도 합산
    {
      let sumDeprec = 0;
      let hasDeprec = false;
      let monthlyDeprecAmount = 0; // 누적 계산용 단위 월 감가액

      // 1순위: 백엔드가 upfront_monthly_deprec 제공 시
      // editRows 전체 사용 - 매출 없는 달도 감가는 계속되므로 rowsForTotal 제외 불가
      editRows.forEach((r) => {
        const n = Number(r.upfront_monthly_deprec);
        if (Number.isFinite(n) && n > 0) {
          sumDeprec += n;
          hasDeprec = true;
          if (!monthlyDeprecAmount) monthlyDeprecAmount = n;
        }
      });

      // 2순위: 개별 거래처 + 백엔드 값 없을 때 → 연속 월 잔존가치 차이로 역산
      if (!hasDeprec && selectedAccountId && selectedAccountId !== "ALL") {
        const sorted = [...editRows]
          .filter(r => Number(r.upfront_cost) > 0)
          .sort((a, b) => Number(a.month) - Number(b.month));
        if (sorted.length >= 2) {
          for (let i = 0; i < sorted.length - 1; i++) {
            const diff = Number(sorted[i].upfront_cost) - Number(sorted[i + 1].upfront_cost);
            if (diff > 0) {
              sumDeprec = diff * sorted.length;
              monthlyDeprecAmount = diff;
              hasDeprec = true;
              break;
            }
          }
        }
      }

      // 누적: 계약 시작이 조회 연도 이전이면 이전 연도 감가액 추가
      // upfront_original_cost 있으면 (원본금액 / 계약총월) × 누적월로 round 오차 제거
      // 없으면 monthlyDeprecAmount × 개월수 fallback
      if (hasDeprec) {
        const parseCS = (s) => s ? { y: parseInt(s.substring(0, 4)), m: parseInt(s.substring(5, 7)) } : null;

        if (selectedAccountId && selectedAccountId !== "ALL") {
          const account = accountList.find(a => String(a.account_id) === String(selectedAccountId));
          const cs = parseCS(account?.contract_start);
          if (cs && cs.y < year) {
            const prevMonths = (year - cs.y) * 12 + (1 - cs.m);
            if (prevMonths > 0) {
              const originalCost = Number(editRows.find(r => Number(r.upfront_original_cost) > 0)?.upfront_original_cost ?? 0);
              const ce = parseCS(account?.contract_end);
              if (originalCost > 0 && ce) {
                const contractTotalMonths = (ce.y - cs.y) * 12 + (ce.m - cs.m) + 1;
                const currentYearMonths = editRows.filter(r => Number(r.upfront_monthly_deprec) > 0).length;
                sumDeprec = Math.round((originalCost / contractTotalMonths) * (prevMonths + currentYearMonths));
              } else {
                sumDeprec += prevMonths * monthlyDeprecAmount;
              }
            }
          }
        } else if (selectedAccountId === "ALL") {
          const byAccountId = new Map();
          editRows.forEach(r => {
            const aid = String(r.account_id ?? "");
            if (!aid) return;
            if (!byAccountId.has(aid)) byAccountId.set(aid, []);
            byAccountId.get(aid).push(r);
          });
          byAccountId.forEach((accRows, aid) => {
            const accMonthlyDeprec = Number(accRows.find(r => Number(r.upfront_monthly_deprec) > 0)?.upfront_monthly_deprec ?? 0);
            if (accMonthlyDeprec <= 0) return;
            const account = accountList.find(a => String(a.account_id) === String(aid));
            const cs = parseCS(account?.contract_start);
            if (!cs || cs.y >= year) return;
            const prevMonths = (year - cs.y) * 12 + (1 - cs.m);
            if (prevMonths <= 0) return;
            const currentYearMonths = accRows.filter(r => Number(r.upfront_monthly_deprec) > 0).length;
            const originalCost = Number(accRows.find(r => Number(r.upfront_original_cost) > 0)?.upfront_original_cost ?? 0);
            const ce = parseCS(account?.contract_end);
            if (originalCost > 0 && ce) {
              const contractTotalMonths = (ce.y - cs.y) * 12 + (ce.m - cs.m) + 1;
              // 기존 현재연도 기여분 제거 후 정밀 누적값으로 교체
              sumDeprec -= accMonthlyDeprec * currentYearMonths;
              sumDeprec += Math.round((originalCost / contractTotalMonths) * (prevMonths + currentYearMonths));
            } else {
              sumDeprec += prevMonths * accMonthlyDeprec;
            }
          });
        }
      }

      if (hasDeprec) {
        result.upfront_cost = sumDeprec;
        const purchaseBase = ["food_process", "dishwasher", "cesco", "water_puri",
          "food_cost", "event_cost", "not_budget_cost", "etc_cost"]
          .reduce((s, f) => s + (Number(result[f]) || 0), 0);
        result.purchase_total = purchaseBase + sumDeprec;
        result.business_profit =
          (result.sales_total || 0) - result.purchase_total -
          (result.person_total || 0) - (result.indirect_total || 0);
        result.total_business_profit = result.business_profit + (result.payback_price || 0);
      }
    }

    // 비율 계산 (프로시저 로직 동일하게 적용)
    // 소계 비율(rnd)은 백엔드와 동일하게 2자리로 반올림해 저장한다.
    // 개별 항목 비율(rndFull)은 소수점 4자리 정밀도를 유지한다 — 화면/엑셀에서는 2자리로 "표시만" 반올림하므로,
    // 화면에 보이는 항목들을 그대로 더해도 소계와 어긋나지 않는다(항목값 자체를 자리 잘라 저장하면 어긋남).
    const rnd = (v, d) => d ? Math.round((v / d) * 100 * 100) / 100 : 0;
    const rndFull = (v, d) => d ? Math.round((v / d) * 100 * 10000) / 10000 : 0;
    const st = result.sales_total || 0;
    const et = result.estimate_total || 0;

    // 인원추산 (분모: estimate_total)
    result.living_estimate_ratio = rndFull(result.living_estimate || 0, et);
    result.basic_estimate_ratio = rndFull(result.basic_estimate || 0, et);
    // 소계 비율은 항목 비율을 더하지 않고, 소계 금액 자체로 계산한다(프로시저와 동일).
    result.estimate_total_ratio = rnd(et, et);

    // 매출 (분모: sales_total)
    result.living_ratio = rndFull(result.living_cost || 0, st);
    result.basic_ratio = rndFull(result.basic_cost || 0, st);
    result.employ_ratio = rndFull(result.employ_cost || 0, st);
    result.living_ratio2 = rndFull(result.living_cost2 || 0, st);
    result.basic_ratio2 = rndFull(result.basic_cost2 || 0, st);
    result.employ_ratio2 = rndFull(result.employ_cost2 || 0, st);
    result.daycare_ratio = rndFull(result.daycare_cost || 0, st);
    result.daycare_emp_ratio = rndFull(result.daycare_emp_cost || 0, st);
    result.integrity_ratio = rndFull(result.integrity_cost || 0, st);
    result.return_ratio = rndFull(result.return_cost || 0, st);
    result.payback_ratio = rndFull(result.payback_price || 0, st);
    // 매출소계 비율은 항목 비율을 더하지 않고, 매출소계 금액(판장금 제외) 자체로 계산한다(프로시저와 동일).
    result.sales_total_ratio = rnd(st, st);

    // 매입 (분모: sales_total)
    result.food_ratio = rndFull(result.food_cost || 0, st);
    result.etc_ratio = rndFull(result.etc_cost || 0, st);
    result.food_trash_ratio = rndFull(result.food_process || 0, st);
    result.dishwasher_ratio = rndFull(result.dishwasher || 0, st);
    result.cesco_ratio = rndFull(result.cesco || 0, st);
    result.water_ratio = rndFull(result.water_puri || 0, st);
    result.event_ratio = rndFull(result.event_cost || 0, st);
    result.not_budget_ratio = rndFull(result.not_budget_cost || 0, st);
    result.upfront_cost_ratio = rndFull(result["upfront_cost"] || 0, st);
    // 매입소계 비율도 항목 비율 합산이 아닌 매입소계 금액 자체로 계산한다(프로시저와 동일).
    result.purchase_total_ratio = rnd(result.purchase_total || 0, st);

    // 인건 (분모: sales_total)
    result.person_ratio = rndFull(result.person_cost || 0, st);
    result.dispatch_ratio = rndFull(result.dispatch_cost || 0, st);
    result.person_total_ratio = rnd(result.person_total || 0, st);

    // 간접 (분모: sales_total)
    result.utility_ratio = rndFull(result.utility_bills || 0, st);
    result.duty_secure_ratio = rndFull(result.duty_secure || 0, st);
    result.etc_indirect_ratio = rndFull(result.etc_indirect_cost || 0, st);
    result.indirect_total_ratio = rnd(result.indirect_total || 0, st);

    // 예외: 빼기 방식 (프로시저와 동일)
    result.business_profit_ratio = Math.round((
      result.sales_total_ratio -
      result.purchase_total_ratio - result.person_total_ratio - result.indirect_total_ratio
    ) * 100) / 100;
    result.total_business_profit_ratio = Math.round((
      result.sales_total_ratio + result.payback_ratio -
      result.purchase_total_ratio - result.person_total_ratio - result.indirect_total_ratio
    ) * 100) / 100;

    return result;
  }, [editRows, selectedAccountId, accountList, year]);

  // ✅ 엑셀 전용 병합 규칙: "거래처 전체 + 특정 월" 다운로드에서, 2026년 5월분부터
  // 강남(SPECIAL_ACCOUNT_ID)의 생계비(2)/일반식대(2)/직원식대(2)(인천강남 마감금액)를
  // 별도 컬럼 없이 강남의 생계비/일반식대/직원식대 칸에 그대로 옮겨 표시한다.
  // (합산 아님, 대체) 개별 거래처 조회·거래처 전체+월 전체 다운로드는 대상이 아니며 기존 로직 그대로 둔다.
  const SALES2_MERGE_YEAR = 2026;
  const SALES2_MERGE_MONTH = 5;
  const isSales2MergeMonth = (y, m) => {
    const yy = Number(y);
    const mm = Number(m);
    if (!Number.isFinite(yy) || !Number.isFinite(mm)) return false;
    return yy > SALES2_MERGE_YEAR || (yy === SALES2_MERGE_YEAR && mm >= SALES2_MERGE_MONTH);
  };
  const SALES2_BASE_TO_EXTRA_VALUE = {
    living_cost: "living_cost2",
    basic_cost: "basic_cost2",
    employ_cost: "employ_cost2",
  };
  const SALES2_EXTRA_VALUE_FIELDS = new Set(Object.values(SALES2_BASE_TO_EXTRA_VALUE));
  const SALES2_BASE_TO_EXTRA_RATIO = {
    living_ratio: "living_ratio2",
    basic_ratio: "basic_ratio2",
    employ_ratio: "employ_ratio2",
  };
  const SALES2_EXTRA_RATIO_FIELDS = new Set(Object.values(SALES2_BASE_TO_EXTRA_RATIO));

  // merge: 호출부(전체 거래처 + 특정 월)에서 이미 연/월 조건(2026-05~)을 확인한 뒤에만 true로 넘긴다.
  const getExcelDisplayValue = (row, field, accountId, merge) => {
    if (!merge || String(accountId ?? "") !== SPECIAL_ACCOUNT_ID) return getDisplayValue(row, field);
    if (SALES2_BASE_TO_EXTRA_VALUE[field]) return getDisplayValue(row, SALES2_BASE_TO_EXTRA_VALUE[field]);
    if (SALES2_EXTRA_VALUE_FIELDS.has(field)) return null;
    return getDisplayValue(row, field);
  };

  const getExcelDisplayRatio = (row, ratioField, accountId, merge) => {
    if (!merge || String(accountId ?? "") !== SPECIAL_ACCOUNT_ID) return getDisplayRatio(row, ratioField);
    if (SALES2_BASE_TO_EXTRA_RATIO[ratioField]) return getDisplayRatio(row, SALES2_BASE_TO_EXTRA_RATIO[ratioField]);
    if (SALES2_EXTRA_RATIO_FIELDS.has(ratioField)) return null;
    return getDisplayRatio(row, ratioField);
  };

  const hasUnsavedChanges = useMemo(() => {
    return (editRows || []).some((row) => {
      const numChanged = editableNumberFields.some((field) => {
        const original = Number(row?._original?.[field] ?? 0);
        const current = Number(row?.[field] ?? 0);
        return original !== current;
      });

      const textChanged = editableTextFields.some((field) => {
        const original = String(row?._original?.[field] ?? "").trim();
        const current = String(row?.[field] ?? "").trim();
        return original !== current;
      });

      return numChanged || textChanged;
    });
  }, [editRows]);

  // ✅ 새로고침: 현재 조건(year, month, 거래처/전체)로 재조회
  const handleRefresh = async () => {
    if (!selectedAccountId) return;

    if (hasUnsavedChanges) {
      const result = await Swal.fire({
        title: "변경사항이 있습니다.",
        text: "새로고침하면 저장되지 않은 변경사항이 사라집니다. 계속할까요?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "새로고침",
        cancelButtonText: "취소",
      });
      if (!result.isConfirmed) return;
    }

    fetchProfitLossTableList(queryAccountId, queryMonth, year, filterAccountIds);
  };

  const getNoteInputWidth = (value) => {
    const safe = String(value ?? "");
    if (!safe) return "80px";

    if (!noteMeasureCanvasRef.current && typeof document !== "undefined") {
      noteMeasureCanvasRef.current = document.createElement("canvas");
    }

    const ctx = noteMeasureCanvasRef.current?.getContext("2d");
    if (!ctx) return `${Math.max(80, safe.length * 10 + 6)}px`;

    ctx.font = "bold 12px sans-serif";
    return `${Math.max(80, Math.ceil(ctx.measureText(safe).width + 6))}px`;
  };

  // ✅ 지난 달 인건비 잠금 여부 (전체 거래처 조회 시 항상 잠금, 과거 월이고 인건비가 0이 아닌 경우 잠금, 임시 해제된 월 제외)
  const isPersonCostLocked = useCallback((row) => {
    if (selectedAccountId === "ALL") return true;  // ✅ 전체 조회 시 항상 잠금
    const rowMonth = Number(row.month);
    const rowYear = Number(year);
    const curYear = today.year();
    const curMonth = today.month() + 1;
    const isPastMonth = rowYear < curYear || (rowYear === curYear && rowMonth < curMonth);
    if (!isPastMonth) return false;
    if (unlockedPersonCostMonths.has(rowMonth)) return false;  // ✅ 임시 잠금해제
    return Number(row._original?.person_cost ?? 0) !== 0;
  }, [selectedAccountId, year, today, unlockedPersonCostMonths]);

  // ✅ 과거 월 인건비 여부 (전체 제외, 과거 월에서만 우클릭 메뉴 표시)
  const isPastPersonCostRow = useCallback((row) => {
    if (selectedAccountId === "ALL") return false;
    const rowMonth = Number(row.month);
    const rowYear = Number(year);
    const curYear = today.year();
    const curMonth = today.month() + 1;
    return rowYear < curYear || (rowYear === curYear && rowMonth < curMonth);
  }, [selectedAccountId, year, today]);

  // ✅ 인건비 셀 우클릭 핸들러 (과거 월이면 항상 메뉴 표시)
  // ✅ 인건비 수정 권한 여부 (department: 3 인사팀, 6)
  const hasPersonCostEditPermission = useMemo(() => {
    const department = localStorage.getItem("department") || "";
    return ["3", "6"].includes(department);
  }, []);

  const handlePersonCostContextMenu = useCallback((e, row) => {
    if (!isPastPersonCostRow(row)) return;
    if (!hasPersonCostEditPermission) return;
    e.preventDefault();
    setCtxMenu({ open: true, mouseX: e.clientX, mouseY: e.clientY, month: Number(row.month) });
  }, [isPastPersonCostRow, hasPersonCostEditPermission]);

  // ✅ 컨텍스트 메뉴 닫기
  const closeCtxMenu = useCallback(() => {
    setCtxMenu({ open: false, mouseX: 0, mouseY: 0, month: null });
  }, []);

  const lockNoteColumn = useCallback(() => {
    if (lockedNoteColumnWidth) return;

    const tableEl = tableBoxRef.current;
    const noteCell = tableEl?.querySelector(`td[data-field="${noteField}"]`);
    const noteHeader = tableEl?.querySelector(`th[data-field="${noteField}"]`);
    const width = Math.ceil((noteCell || noteHeader)?.getBoundingClientRect?.().width ?? 0);

    setLockedNoteColumnWidth(width > 0 ? width : 80);
  }, [lockedNoteColumnWidth, noteField]);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const sanitizeFilename = (name) => {
    const safe = String(name || "")
      .replace(/[\\/:*?"<>|]/g, "_")
      .replace(/\s+/g, " ")
      .trim();
    return safe.length > 100 ? safe.slice(0, 100) : safe;
  };

  const excelBorderThin = {
    top: { style: "thin" },
    left: { style: "thin" },
    bottom: { style: "thin" },
    right: { style: "thin" },
  };

  const excelHeaderFill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFEFEFEF" },
  };

  const detectExcelExtOrError = (arrayBuffer) => {
    const u8 = new Uint8Array(arrayBuffer);
    if (u8.length < 4) return { kind: "unknown" };

    const isXlsx =
      u8[0] === 0x50 && u8[1] === 0x4b && (u8[2] === 0x03 || u8[2] === 0x05 || u8[2] === 0x07);

    const isXls =
      u8.length >= 8 &&
      u8[0] === 0xd0 &&
      u8[1] === 0xcf &&
      u8[2] === 0x11 &&
      u8[3] === 0xe0 &&
      u8[4] === 0xa1 &&
      u8[5] === 0xb1 &&
      u8[6] === 0x1a &&
      u8[7] === 0xe1;

    if (isXlsx) return { kind: "xlsx" };
    if (isXls) return { kind: "xls" };

    let preview = "";
    try {
      preview = new TextDecoder("utf-8").decode(u8.slice(0, 400));
    } catch (e) {
      preview = "(텍스트 디코딩 실패)";
    }
    return { kind: "not_excel", preview };
  };

  const excelApi = axios.create({
    baseURL: api.defaults.baseURL,
    withCredentials: api.defaults.withCredentials,
    timeout: 60000,
    transformResponse: [(d) => d],
  });

  const arrayBufferToJson = (ab) => {
    const text = new TextDecoder("utf-8").decode(new Uint8Array(ab));
    return JSON.parse(text);
  };

  const toPercentCell = (v) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    return n / 100;
  };

  // 모든 클라이언트 생성 엑셀에 동일한 판장금 제외 기준을 적용한다.
  const normalizeProfitLossRowForExcel = (sourceRow) => {
    const row = { ...(sourceRow || {}) };
    const amount = (field) => Number(row[field]) || 0;
    const rnd = (value, denominator) =>
      denominator ? Math.round((value / denominator) * 100 * 100) / 100 : 0;
    // 개별 항목 비율은 4자리 정밀도로 유지(표시만 2자리로 반올림) — 화면에 보이는 항목 합이 소계와 어긋나지 않도록.
    const rndFull = (value, denominator) =>
      denominator ? Math.round((value / denominator) * 100 * 10000) / 10000 : 0;

    // sales_total은 구성 항목으로 다시 계산하지 않고 DB 저장값을 그대로 쓴다(단일 행이라 재계산 불필요,
    // 재계산하면 daycare 조건부 포함 등 프로시저 로직과 어긋날 위험만 있음).
    const salesTotal = amount("sales_total");

    [
      ["living_cost", "living_ratio"],
      ["basic_cost", "basic_ratio"],
      ["employ_cost", "employ_ratio"],
      ["living_cost2", "living_ratio2"],
      ["basic_cost2", "basic_ratio2"],
      ["employ_cost2", "employ_ratio2"],
      ["daycare_cost", "daycare_ratio"],
      ["daycare_emp_cost", "daycare_emp_ratio"],
      ["integrity_cost", "integrity_ratio"],
      ["return_cost", "return_ratio"],
      ["payback_price", "payback_ratio"],
      ["food_cost", "food_ratio"],
      ["etc_cost", "etc_ratio"],
      ["food_process", "food_trash_ratio"],
      ["dishwasher", "dishwasher_ratio"],
      ["cesco", "cesco_ratio"],
      ["water_puri", "water_ratio"],
      ["event_cost", "event_ratio"],
      ["not_budget_cost", "not_budget_ratio"],
      ["person_cost", "person_ratio"],
      ["dispatch_cost", "dispatch_ratio"],
      ["utility_bills", "utility_ratio"],
      ["duty_secure", "duty_secure_ratio"],
      ["etc_indirect_cost", "etc_indirect_ratio"],
    ].forEach(([valueKey, ratioKey]) => {
      row[ratioKey] = rndFull(amount(valueKey), salesTotal);
    });

    // 소계 비율은 항목 비율을 더하지 않고, 소계 금액 자체로 계산한다(프로시저와 동일).
    row.sales_total_ratio = rnd(salesTotal, salesTotal);
    row.purchase_total_ratio = rnd(row.purchase_total, salesTotal);
    row.person_total_ratio = rnd(row.person_total, salesTotal);
    row.indirect_total_ratio = rnd(row.indirect_total, salesTotal);
    // 영업이익/총 영업이익은 값과 비율 모두 재계산하지 않고 DB 값을 그대로 사용한다.

    return row;
  };

  const buildTwoRowHeader = (sheet, firstTitle, firstKey) => {
    const subCols = filteredHeaders.flatMap((h) => h.cols);
    const secondRowHeaders = [firstTitle, ...subCols];

    sheet.columns = [
      { key: firstKey, width: firstTitle === "거래처" ? 22 : 10 },
      ...subCols.map((col) => ({
        key: fieldMap[col]?.value || col,
        width: col === "비고" ? 30 : col === "매출소계(판장금제외)" ? 20 : 14,
      })),
    ];

    const topRowValues = new Array(secondRowHeaders.length).fill(null);
    topRowValues[0] = firstTitle;

    let colIdx = 2;
    filteredHeaders.forEach((h) => {
      if (!h.group) {
        h.cols.forEach((col, idx) => {
          topRowValues[colIdx - 1 + idx] = col;
          secondRowHeaders[colIdx - 1 + idx] = null;
        });
      } else {
        topRowValues[colIdx - 1] = h.group;
      }
      colIdx += h.cols.length;
    });

    sheet.addRow(topRowValues);
    sheet.addRow(secondRowHeaders);

    sheet.mergeCells(1, 1, 2, 1);

    let start = 2;
    filteredHeaders.forEach((h) => {
      const end = start + h.cols.length - 1;
      if (!h.group) {
        for (let colNo = start; colNo <= end; colNo += 1) {
          sheet.mergeCells(1, colNo, 2, colNo);
        }
      } else {
        sheet.mergeCells(1, start, 1, end);
      }
      start = end + 1;
    });

    [1, 2].forEach((rno) => {
      const r = sheet.getRow(rno);
      r.height = 18;
      r.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.fill = excelHeaderFill;
        cell.border = excelBorderThin;
      });
    });

    // 헤더가 2행이므로 2행까지 틀 고정
    sheet.views = [{ state: "frozen", xSplit: 0, ySplit: 2 }];
  };

  const monthExcelRowDefs = [
    {
      group: "인원",
      label: "인원추산(생계비)",
      valueKey: "living_estimate",
      ratioKey: "living_estimate_ratio",
    },
    {
      group: "인원",
      label: "인원추산(일반)",
      valueKey: "basic_estimate",
      ratioKey: "basic_estimate_ratio",
    },
    {
      group: "인원",
      label: "인원추산(합계)",
      valueKey: "estimate_total",
      ratioKey: "estimate_total_ratio",
    },
    { __blank: true },
    { group: "매출", label: "생계비", valueKey: "living_cost", ratioKey: "living_ratio" },
    { group: "매출", label: "일반식대", valueKey: "basic_cost", ratioKey: "basic_ratio" },
    { group: "매출", label: "직원식대", valueKey: "employ_cost", ratioKey: "employ_ratio" },
    { group: "매출", label: "주간보호", valueKey: "daycare_cost", ratioKey: "daycare_ratio" },
    {
      group: "매출",
      label: "주간보호직원",
      valueKey: "daycare_emp_cost",
      ratioKey: "daycare_emp_ratio",
    },
    { group: "매출", label: "보전", valueKey: "integrity_cost", ratioKey: "integrity_ratio" },
    { group: "매출", label: "반환금", valueKey: "return_cost", ratioKey: "return_ratio" },
    { group: "매출", label: "소계(매출)", valueKey: "sales_total", ratioKey: "sales_total_ratio" },
    { __blank: true },
    { group: "매입", label: "식자재", valueKey: "food_cost", ratioKey: "food_ratio" },
    { group: "매입", label: "기타경비", valueKey: "etc_cost", ratioKey: "etc_ratio" },
    { group: "매입", label: "음식물처리", valueKey: "food_process", ratioKey: "food_trash_ratio" },
    { group: "매입", label: "식기세척기", valueKey: "dishwasher", ratioKey: "dishwasher_ratio" },
    { group: "매입", label: "세스코방제", valueKey: "cesco", ratioKey: "cesco_ratio" },
    { group: "매입", label: "정수기임대", valueKey: "water_puri", ratioKey: "water_ratio" },
    { group: "매입", label: "이벤트", valueKey: "event_cost", ratioKey: "event_ratio" },
    {
      group: "매입",
      label: "예산미발행",
      valueKey: "not_budget_cost",
      ratioKey: "not_budget_ratio",
    },
    {
      group: "매입",
      label: "소계(매입)",
      valueKey: "purchase_total",
      ratioKey: "purchase_total_ratio",
    },
    { __blank: true },
    { group: "인건", label: "인건비확보", valueKey: "person_cost", ratioKey: "person_ratio" },
    { group: "인건", label: "파출비", valueKey: "dispatch_cost", ratioKey: "dispatch_ratio" },
    {
      group: "인건",
      label: "소계(인건)",
      valueKey: "person_total",
      ratioKey: "person_total_ratio",
    },
    { __blank: true },
    { group: "간접", label: "수도광열비", valueKey: "utility_bills", ratioKey: "utility_ratio" },
    { group: "간접", label: "비고", valueKey: "utility_bills_note", ratioKey: null },
    { group: "간접", label: "세금확보", valueKey: "duty_secure", ratioKey: "duty_secure_ratio" },
    {
      group: "간접",
      label: "소계(간접)",
      valueKey: "indirect_total",
      ratioKey: "indirect_total_ratio",
    },
    {
      group: "",
      label: "영업이익",
      valueKey: "business_profit",
      ratioKey: "business_profit_ratio",
    },
    { group: "", label: "판장금", valueKey: "payback_price", ratioKey: "payback_ratio" },
    {
      group: "",
      label: "총 영업이익",
      valueKey: "total_business_profit",
      ratioKey: "total_business_profit_ratio",
    },
    { __blank: true },
  ];

  const monthExcelRowDefsSales2 = [
    { group: "매출", label: "생계비(2)", valueKey: "living_cost2", ratioKey: "living_ratio2" },
    { group: "매출", label: "일반식대(2)", valueKey: "basic_cost2", ratioKey: "basic_ratio2" },
    { group: "매출", label: "직원식대(2)", valueKey: "employ_cost2", ratioKey: "employ_ratio2" },
  ];

  const isMonthlyRecordArray = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return false;
    const sample = rows.find((r) => r && typeof r === "object");
    if (!sample) return false;
    const hasMonth = "month" in sample || "mm" in sample || "mon" in sample;
    const hasKnownValueKey = Object.values(fieldMap).some((m) => m?.value && m.value in sample);
    return Boolean(hasMonth && hasKnownValueKey);
  };

  const getMonthNumber = (r) => {
    const v = r?.month ?? r?.mm ?? r?.mon;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const buildMonthAllExcelLikeTemplate = async (rows) => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("손익표(월-전체)");
    const header = [
      "NO",
      "C분류",
      "E분류",
      "F분류",
      "당월",
      "비율",
      "1월",
      "비율",
      "2월",
      "비율",
      "3월",
      "비율",
      "4월",
      "비율",
      "5월",
      "비율",
      "6월",
      "비율",
      "7월",
      "비율",
      "8월",
      "비율",
      "9월",
      "비율",
      "10월",
      "비율",
      "11월",
      "비율",
      "12월",
      "비율",
      "합계",
      "비율",
    ];

    sheet.columns = header.map((h, idx) => {
      const w =
        idx === 0
          ? 6
          : idx === 1
            ? 18
            : idx === 2
              ? 12
              : idx === 3
                ? 18
                : idx === 4
                  ? 16
                  : idx === 5
                    ? 9
                    : idx >= 6
                      ? idx % 2 === 0 ? 18 : 9
                      : 12;
      return { header: h, key: `c${idx + 1}`, width: w };
    });

    const hr = sheet.getRow(1);
    hr.height = 18;
    hr.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.fill = excelHeaderFill;
      cell.border = excelBorderThin;
    });

    // 헤더가 1행이므로 1행까지 틀 고정
    sheet.views = [{ state: "frozen", xSplit: 0, ySplit: 1 }];

    const byAccount = new Map();
    rows.map(normalizeProfitLossRowForExcel).forEach((r) => {
      const aid = String(r?.account_id ?? r?.account_name ?? "");
      if (!aid) return;
      if (!byAccount.has(aid)) byAccount.set(aid, []);
      byAccount.get(aid).push(r);
    });

    const currentMonth = year === today.year() ? today.month() + 1 : 12;
    let no = 1;

    const putBlankRow = () => {
      const blank = new Array(header.length).fill(null);
      sheet.addRow(blank);
    };

    for (const [, accRows] of byAccount.entries()) {
      const accId = String(accRows[0]?.account_id ?? "");
      const defs =
        accId === SPECIAL_ACCOUNT_ID
          ? (() => {
            const out = [];
            for (const d of monthExcelRowDefs) {
              out.push(d);
              if (d.label === "생계비") out.push(monthExcelRowDefsSales2[0]);
              if (d.label === "일반식대") out.push(monthExcelRowDefsSales2[1]);
              if (d.label === "직원식대") out.push(monthExcelRowDefsSales2[2]);
            }
            return out;
          })()
          : monthExcelRowDefs;

      const monthMap = new Map();
      accRows.forEach((r) => {
        const m = getMonthNumber(r);
        if (m) monthMap.set(m, normalizeProfitLossRowForExcel(r));
      });

      const accName = accRows[0]?.account_name ?? "";

      for (const def of defs) {
        if (def.__blank) {
          putBlankRow();
          continue;
        }

        const row = new Array(header.length).fill(null);
        row[0] = no++;
        row[1] = accName || null;
        row[2] = def.group || null;
        row[3] = def.label || null;

        const setValueRatioAt = (valueColIdx0, value, ratio) => {
          row[valueColIdx0] = value ?? null;
          row[valueColIdx0 + 1] = ratio ?? null;
        };

        const curRec = monthMap.get(currentMonth);
        const curVal = curRec ? getDisplayValue(curRec, def.valueKey) : null;
        const curRatio = def.ratioKey && curRec ? toPercentCell(getDisplayRatio(curRec, def.ratioKey)) : null;
        setValueRatioAt(4, curVal, curRatio);

        for (let m = 1; m <= 12; m++) {
          const rec = monthMap.get(m);
          const val = rec ? getDisplayValue(rec, def.valueKey) : null;
          const ratio = def.ratioKey && rec ? toPercentCell(getDisplayRatio(rec, def.ratioKey)) : null;
          const base = 6 + (m - 1) * 2;
          setValueRatioAt(base, val, ratio);
        }

        if (def.valueKey && def.valueKey !== "utility_bills_note") {
          let sum = 0;
          let hasAny = false;
          for (let m = 1; m <= 12; m++) {
            const rec = monthMap.get(m);
            if (!hasBaseSalesCost(rec)) continue;
            const v = rec ? getDisplayValue(rec, def.valueKey) : null;
            const n = Number(v);
            if (Number.isFinite(n)) {
              const salesTotalWithoutPayback = [
                "living_cost",
                "basic_cost",
                "employ_cost",
                "living_cost2",
                "basic_cost2",
                "employ_cost2",
                "daycare_cost",
                "daycare_emp_cost",
                "integrity_cost",
                "return_cost",
              ].reduce(
                (total, field) => total + (Number(getDisplayValue(rec, field)) || 0),
                0
              );
              sum += def.valueKey === "sales_total" ? salesTotalWithoutPayback : n;
              hasAny = true;
            }
          }
          row[30] = hasAny ? sum : null;
          row[31] = null;
        }

        sheet.addRow(row);
      }
    }

    const excelGrayFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE6E6E6" } };
    const excelSkyFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFDDEEFF" } };
    const excelRedFont = { color: { argb: "FFFF0000" } };

    const valueCols = new Set([5, 7, 9, 11, 13, 15, 17, 19, 21, 23, 25, 27, 29, 31]);
    const ratioCols = new Set([6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32]);

    sheet.eachRow((r, rno) => {
      if (rno === 1) return;

      const fLabel = String(r.getCell(4).value ?? "");
      const isSoGyeRow = fLabel.includes("소계");
      const isProfitRow = fLabel.includes("영업이익") || fLabel.includes("영엽이익");
      const isSummaryRow = isSoGyeRow || fLabel.includes("합계") || isProfitRow;

      r.eachCell((cell, cno) => {
        cell.border = excelBorderThin;

        if (isSummaryRow) cell.fill = excelGrayFill;
        if (cno === 2) cell.fill = excelSkyFill;

        if (cno === 4 && isSoGyeRow) {
          cell.fill = excelSkyFill;
          cell.font = { ...(cell.font || {}), ...excelRedFont, bold: true };
        }

        if (isProfitRow) {
          cell.fill = excelSkyFill;
          cell.font = { ...(cell.font || {}), bold: true };
        }

        if (cno <= 4) {
          cell.alignment = { vertical: "middle", horizontal: cno === 4 ? "left" : "center" };
          return;
        }

        if (ratioCols.has(cno)) {
          if (typeof cell.value === "number") cell.numFmt = "0.0%";
          cell.alignment = { vertical: "middle", horizontal: "center" };
          return;
        }

        if (valueCols.has(cno)) {
          if (typeof cell.value === "number") {
            const isEstimateRow = fLabel.includes("인원추산");
            cell.numFmt = isEstimateRow ? "0.0" : "#,##0";
            cell.alignment = { vertical: "middle", horizontal: "right" };
          } else {
            cell.alignment = { vertical: "middle", horizontal: "center" };
          }
          return;
        }

        cell.alignment = { vertical: "middle", horizontal: "center" };
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    return buffer;
  };

  // ✅ 공통: 행 데이터로 2줄(값/비율) 엑셀 시트 만들기
  // applyExcelSales2Merge: "거래처 전체 + 특정 월" 다운로드에서만 true로 넘겨,
  // 강남의 생계비(2)/일반식대(2)/직원식대(2)(인천강남 마감금액, 2026년 5월분부터)를
  // 생계비/일반식대/직원식대 칸으로 옮겨 보여준다. 그 외(개별 거래처 다운로드 등)는
  // 기본값 false로 기존 로직 그대로 둔다.
  const addValueRatioSheet = (
    workbook,
    {
      rows,
      sheetName,
      firstTitle,
      firstKey,
      labelGetter,
      applyExcelSales2Merge = false,
      blankValueKeys = [],
    }
  ) => {
    const existingSheet = workbook.getWorksheet(sheetName);
    if (existingSheet) {
      workbook.removeWorksheet(existingSheet.id);
    }

    const sheet = workbook.addWorksheet(sheetName);

    buildTwoRowHeader(sheet, firstTitle, firstKey);

    const ratioRowNumbers = new Set();
    const totalRowNumbers = new Set();
    const blankKeySet = new Set(blankValueKeys);

    rows.forEach((r) => {
      const rowLabel = labelGetter(r);
      const valueObj = {
        [firstKey]: rowLabel,
      };

      const rowAccountId = r?.account_id ?? selectedAccountId;

      filteredHeaders.forEach((h) => {
        h.cols.forEach((col) => {
          const key = fieldMap[col]?.value || col;
          valueObj[key] = blankKeySet.has(key)
            ? ""
            : getExcelDisplayValue(r, key, rowAccountId, applyExcelSales2Merge) ?? "";
        });
      });

      sheet.addRow(valueObj);

      const valueRowNo = sheet.lastRow.number;

      const ratioObj = { [firstKey]: "" };

      filteredHeaders.forEach((h) => {
        h.cols.forEach((col) => {
          const ratioKey = fieldMap[col]?.ratio;
          const valueKey = fieldMap[col]?.value || col;
          ratioObj[valueKey] = ratioKey
            ? toPercentCell(getExcelDisplayRatio(r, ratioKey, rowAccountId, applyExcelSales2Merge))
            : "";
        });
      });

      sheet.addRow(ratioObj);

      const ratioRowNo = sheet.lastRow.number;
      ratioRowNumbers.add(ratioRowNo);

      if (rowLabel === "합계") {
        totalRowNumbers.add(valueRowNo);
        totalRowNumbers.add(ratioRowNo);
      }

      sheet.mergeCells(valueRowNo, 1, ratioRowNo, 1);
    });

    const estimateHeaders = new Set(["생계인원", "일반인원", "인원합계"]);
    const estimateCols = new Set();
    sheet.getRow(2).eachCell((cell, colNo) => {
      if (estimateHeaders.has(String(cell.value ?? ""))) estimateCols.add(colNo);
    });

    const excelTotalRowFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF9E6" } };

    sheet.eachRow((row, rowNumber) => {
      row.eachCell((cell, colNumber) => {
        cell.border = excelBorderThin;
        if (totalRowNumbers.has(rowNumber)) cell.fill = excelTotalRowFill;
        if (rowNumber <= 2) return;

        const headerText = sheet.getRow(2).getCell(colNumber).value;

        if (headerText === firstTitle || headerText === "월" || headerText === "거래처") {
          cell.alignment = { vertical: "middle", horizontal: "center" };
          return;
        }

        if (headerText === "비고") {
          cell.alignment = { vertical: "middle", horizontal: "left" };
          return;
        }

        if (ratioRowNumbers.has(rowNumber)) {
          if (typeof cell.value === "number") cell.numFmt = "0.0%";
          cell.alignment = { vertical: "middle", horizontal: "center" };
          return;
        }

        if (typeof cell.value === "number") {
          cell.numFmt = estimateCols.has(colNumber) ? "0.0" : "#,##0";
          cell.alignment = { vertical: "middle", horizontal: "right" };
        } else {
          cell.alignment = { vertical: "middle", horizontal: "center" };
        }
      });
    });

    // 그룹 경계 컬럼 좌우 테두리 굵게
    const thickSide = { style: "medium" };
    let groupColStart = 2;
    filteredHeaders.forEach((h) => {
      const groupEnd = groupColStart + h.cols.length - 1;
      if (h.group) {
        sheet.eachRow((row) => {
          const lCell = row.getCell(groupColStart);
          const rCell = row.getCell(groupEnd);
          lCell.border = { ...(lCell.border || {}), left: thickSide };
          rCell.border = { ...(rCell.border || {}), right: thickSide };
        });
      } else {
        h.cols.forEach((_, idx) => {
          const colNo = groupColStart + idx;
          sheet.eachRow((row) => {
            const cell = row.getCell(colNo);
            cell.border = { ...(cell.border || {}), left: thickSide, right: thickSide };
          });
        });
      }
      groupColStart = groupEnd + 1;
    });

    return sheet;
  };

  // ✅ 공통: 행 데이터로 2줄(값/비율) 엑셀 만들기
  const buildValueRatioWorkbook = async ({
    rows,
    sheetName,
    firstTitle,
    firstKey,
    labelGetter,
    applyExcelSales2Merge = false,
  }) => {
    const workbook = new ExcelJS.Workbook();
    addValueRatioSheet(workbook, {
      rows,
      sheetName,
      firstTitle,
      firstKey,
      labelGetter,
      applyExcelSales2Merge,
    });

    return workbook.xlsx.writeBuffer();
  };

  const appendCalculatedTotalRow = (rows) => {
    if (!Array.isArray(rows)) return [];
    if (!totalRow || rows.length === 0) return rows;
    return [...rows, { ...totalRow, __isCalculatedTotal: true }];
  };

  const addMonthTotalSheet = (workbook, rows) => {
    return addValueRatioSheet(workbook, {
      rows: appendCalculatedTotalRow(rows),
      sheetName: "손익표(월-전체)",
      firstTitle: "월",
      firstKey: "__month",
      labelGetter: (r) =>
        r.__isCalculatedTotal ? "합계" : `${r.month ?? r.mm ?? r.mon ?? ""}월`,
      // 월-전체(전 거래처 합계) 시트의 비고는 특정 거래처 한 곳의 메모가
      // 섞여 나오는 것이라 의미가 없으므로 항상 빈 값으로 표시한다.
      blankValueKeys: ["utility_bills_note"],
    });
  };

  const getMonthTotalRowsForExcel = async () => {
    if (Array.isArray(editRows) && editRows.length > 0) return editRows;

    const res = await api.get("/HeadOffice/ProfitLossTableList", {
      params: {
        year,
        account_id: selectedAccountId,
        month: "",
      },
    });

    return Array.isArray(res.data) ? res.data : [];
  };

  const setWorksheetName = (workbook, worksheet, sheetName) => {
    const sameNameSheet = workbook.getWorksheet(sheetName);
    if (sameNameSheet && sameNameSheet.id !== worksheet.id) {
      workbook.removeWorksheet(sameNameSheet.id);
    }

    worksheet.name = sheetName;
  };

  const widenMonthAllCurrentColumn = (workbook) => {
    const sheet = workbook.getWorksheet("손익표(월-전체)") || workbook.worksheets[0];
    if (!sheet) return;

    const currentColumn = sheet.getColumn(6);
    const currentWidth = Number(currentColumn.width ?? 0);
    currentColumn.width = Math.max(currentWidth, 16);
  };

  const applyCalculationRulesToDownloadedWorkbook = (workbook) => {
    const salesLabels = new Set(["매출소계(판장금제외)", "소계(매출)", "매출소계"]);
    const componentLabels = new Set([
      "생계비", "일반식대", "직원식대", "생계비(2)", "일반식대(2)", "직원식대(2)",
      "주간일반", "주간직원", "주간보호", "주간보호직원", "보전", "반환금",
    ]);
    const textOf = (cell) => String(cell?.value?.text ?? cell?.value ?? "").trim();
    const numberOf = (cell) => {
      const value = cell?.value?.result ?? cell?.value;
      const number = Number(String(value ?? "").replace(/,/g, ""));
      return Number.isFinite(number) ? number : 0;
    };

    workbook.worksheets.forEach((sheet) => {
      // 가로형 엑셀: 거래처/월이 행, 손익 항목이 열인 형식
      let horizontalHeader = null;
      sheet.eachRow((row) => {
        if (horizontalHeader) return;
        const labels = new Map();
        row.eachCell((cell, colNumber) => labels.set(textOf(cell), colNumber));
        const salesCol = [...salesLabels].map((label) => labels.get(label)).find(Boolean);
        if (labels.get("생계비") && labels.get("일반식대") && salesCol) {
          horizontalHeader = { rowNumber: row.number, labels, salesCol };
        }
      });

      if (horizontalHeader) {
        const componentCols = [...componentLabels]
          .map((label) => horizontalHeader.labels.get(label))
          .filter(Boolean);
        const totalRows = [];
        const eligibleValueRows = [];

        for (let rowNumber = horizontalHeader.rowNumber + 1; rowNumber <= sheet.rowCount; rowNumber++) {
          const row = sheet.getRow(rowNumber);
          const isTotalRow = row.values.some((value) => {
            const text = String(value?.text ?? value ?? "").trim();
            return text === "합계" || text === "총합계";
          });
          if (isTotalRow) {
            const totalLiving = numberOf(row.getCell(horizontalHeader.labels.get("생계비")));
            const totalBasic = numberOf(row.getCell(horizontalHeader.labels.get("일반식대")));
            if (Math.max(Math.abs(totalLiving), Math.abs(totalBasic)) > 1) {
              totalRows.push(rowNumber);
            }
            continue;
          }

          const living = numberOf(row.getCell(horizontalHeader.labels.get("생계비")));
          const basic = numberOf(row.getCell(horizontalHeader.labels.get("일반식대")));
          const hasBaseSales = living !== 0 || basic !== 0;
          if (!hasBaseSales) continue;

          // 금액 행과 바로 아래 비율 행을 구분한다.
          if (Math.max(Math.abs(living), Math.abs(basic)) > 1) {
            eligibleValueRows.push(rowNumber);
          }

          row.getCell(horizontalHeader.salesCol).value = componentCols.reduce(
            (sum, col) => sum + numberOf(row.getCell(col)),
            0
          );
        }

        // 서버가 만든 합계 행도 유효한 월의 금액 행만 사용해 다시 계산한다.
        totalRows.forEach((totalRowNumber) => {
          const excelTotalRow = sheet.getRow(totalRowNumber);
          horizontalHeader.labels.forEach((_, label) => {
            const col = horizontalHeader.labels.get(label);
            // 초기투자비용은 잔존가치 합이 아닌 누적 감가액이어야 하므로 별도 처리
            if (!col || col === 1 || label === "비고" || label === "초기투자비용") return;
            const hasNumericValue = eligibleValueRows.some(
              (rowNumber) => numberOf(sheet.getCell(rowNumber, col)) !== 0
            );
            if (!hasNumericValue) return;
            excelTotalRow.getCell(col).value = eligibleValueRows.reduce(
              (sum, rowNumber) => sum + numberOf(sheet.getCell(rowNumber, col)),
              0
            );
          });

          // 초기투자비용 합계: totalRow(useMemo)의 누적 계산값 사용 (합계 행이 하나인 시트만)
          const upfrontCol = horizontalHeader.labels.get("초기투자비용");
          if (upfrontCol && totalRows.length === 1 && totalRow?.upfront_cost != null) {
            excelTotalRow.getCell(upfrontCol).value = totalRow.upfront_cost;
          }

          excelTotalRow.getCell(horizontalHeader.salesCol).value = componentCols.reduce(
            (sum, col) => sum + numberOf(excelTotalRow.getCell(col)),
            0
          );
        });
      }

      // 세로형 월-전체 엑셀: 항목이 행, 각 월과 합계가 열인 형식
      let headerRow = null;
      let totalCol = null;
      const monthCols = new Map();
      sheet.eachRow((row) => {
        if (headerRow) return;
        row.eachCell((cell, colNumber) => {
          const text = textOf(cell);
          const match = text.match(/^(\d{1,2})월$/);
          if (match) monthCols.set(Number(match[1]), colNumber);
          if (text === "합계") totalCol = colNumber;
        });
        if (monthCols.size === 12 && totalCol) headerRow = row.number;
      });
      if (!headerRow || !totalCol) return;

      let labelCol = null;
      for (let col = 1; col < totalCol; col++) {
        let score = 0;
        for (let row = headerRow + 1; row <= sheet.rowCount; row++) {
          const label = textOf(sheet.getCell(row, col));
          if (label === "생계비" || label === "일반식대" || salesLabels.has(label)) score++;
        }
        if (score >= 3) {
          labelCol = col;
          break;
        }
      }
      if (!labelCol) return;

      const blockStarts = [];
      for (let row = headerRow + 1; row <= sheet.rowCount; row++) {
        if (textOf(sheet.getCell(row, labelCol)) === "인원추산(생계비)") blockStarts.push(row);
      }
      blockStarts.forEach((start, index) => {
        const end = (blockStarts[index + 1] || sheet.rowCount + 1) - 1;
        const rowByLabel = new Map();
        for (let row = start; row <= end; row++) {
          const label = textOf(sheet.getCell(row, labelCol));
          if (label) rowByLabel.set(label, row);
        }
        const livingRow = rowByLabel.get("생계비");
        const basicRow = rowByLabel.get("일반식대");
        if (!livingRow || !basicRow) return;

        const eligibleMonthCols = [...monthCols.values()].filter(
          (col) =>
            numberOf(sheet.getCell(livingRow, col)) !== 0 ||
            numberOf(sheet.getCell(basicRow, col)) !== 0
        );
        for (let row = start; row <= end; row++) {
          const hasMonthlyNumber = [...monthCols.values()].some(
            (col) => numberOf(sheet.getCell(row, col)) !== 0
          );
          if (!hasMonthlyNumber) continue;
          sheet.getCell(row, totalCol).value = eligibleMonthCols.reduce(
            (sum, col) => sum + numberOf(sheet.getCell(row, col)),
            0
          );
        }

        const salesRow = [...salesLabels]
          .map((label) => rowByLabel.get(label))
          .find(Boolean);
        if (salesRow) {
          sheet.getCell(salesRow, totalCol).value = [...componentLabels].reduce(
            (sum, label) => {
              const row = rowByLabel.get(label);
              return sum + (row ? numberOf(sheet.getCell(row, totalCol)) : 0);
            },
            0
          );
        }
      });
    });
  };

  const convertLegacyXlsWithCalculationRules = async (arrayBuffer) => {
    const legacyWorkbook = XLSX.read(arrayBuffer, { type: "array" });
    const convertedBuffer = XLSX.write(legacyWorkbook, { bookType: "xlsx", type: "array" });
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(convertedBuffer);
    applyCalculationRulesToDownloadedWorkbook(workbook);
    return workbook.xlsx.writeBuffer();
  };

  const handleExcelDownload = async () => {
    if (!selectedAccountId) return;

    if (hasUnsavedChanges) {
      Swal.fire("저장 후 다운로드하세요.", "수정된 내용이 있습니다. 저장 후 엑셀 다운로드해주세요.", "warning");
      return;
    }

    const isAllMonth = String(month) === "ALL";
    const isAllAccount = String(selectedAccountId) === "ALL";
    const accountName = selectedAccount?.account_name || "거래처";

    try {
      // ✅ 1) 월이 선택된 경우 -> 월 엑셀 우선
      if (!isAllMonth) {
        // ✅ 전체 거래처 + 특정 월
        if (isAllAccount) {
          const res = await excelApi.get("/HeadOffice/ExcelDownMonthProfitLossTableList", {
            params: { account_id: selectedAccountId, year, month: queryMonth },
            responseType: "arraybuffer",
            headers: {
              Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            },
          });

          const detected = detectExcelExtOrError(res.data);

          if (detected.kind === "xlsx") {
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(res.data);
            applyCalculationRulesToDownloadedWorkbook(workbook);
            const buffer = await workbook.xlsx.writeBuffer();
            downloadBlob(
              new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              }),
              `${year}-${queryMonth}월(손익표).xlsx`
            );
            return;
          }

          if (detected.kind === "xls") {
            const buffer = await convertLegacyXlsWithCalculationRules(res.data);
            downloadBlob(
              new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              }),
              `${year}-${queryMonth}월(손익표).xlsx`
            );
            return;
          }

          let rows;
          try {
            rows = arrayBufferToJson(res.data);
          } catch (e) {
            Swal.fire(
              "엑셀 다운로드 실패",
              `서버가 엑셀도 아니고 JSON 파싱도 실패했습니다.\n\n(앞부분)\n${detected.preview || ""
              }`,
              "error"
            );
            return;
          }

          if (!Array.isArray(rows) || rows.length === 0) {
            Swal.fire("다운로드할 데이터가 없습니다.", "", "info");
            return;
          }

          const buffer = await buildValueRatioWorkbook({
            rows,
            sheetName: `${queryMonth}월 손익표`,
            firstTitle: "거래처",
            firstKey: "__account",
            labelGetter: (r) => String(r?.account_name ?? ""),
            // 거래처 전체 + 특정 월 다운로드는 2026년 5월분부터 강남의 (2) 금액을
            // 생계비/일반식대/직원식대 칸으로 옮겨 보여준다.
            applyExcelSales2Merge: isSales2MergeMonth(year, queryMonth),
          });

          const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          downloadBlob(blob, `${year}-${queryMonth}월(손익표).xlsx`);
          return;
        }

        // ✅ 개별 거래처 + 특정 월
        if (!editRows || editRows.length === 0) {
          Swal.fire("다운로드할 데이터가 없습니다.", "", "info");
          return;
        }

        const buffer = await buildValueRatioWorkbook({
          rows: editRows,
          sheetName: `${queryMonth}월 손익표`,
          firstTitle: "월",
          firstKey: "__month",
          labelGetter: (r) => `${r.month}월`,
        });

        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        downloadBlob(blob, `${year}-${sanitizeFilename(accountName)}-${queryMonth}월.xlsx`);
        return;
      }

      // ✅ 2) 월이 전체인 경우 -> 기존 로직 그대로
      const fileName =
        selectedAccountId === "ALL"
          ? `${year}-손익표.xlsx`
          : `${year}-${sanitizeFilename(accountName)}.xlsx`;

      if (selectedAccountId === "ALL") {
        const { value: mode } = await Swal.fire({
          title: "전체 다운로드 옵션",
          text: "다운로드 방식을 선택하세요.",
          input: "radio",
          inputOptions: {
            account: "거래처 전체",
            month: "월 전체",
          },
          inputValidator: (v) => (!v ? "옵션을 선택해주세요." : undefined),
          confirmButtonText: "다운로드",
          showCancelButton: true,
          cancelButtonText: "취소",
        });

        if (!mode) return;

        if (mode === "account") {
          const res = await excelApi.get("/HeadOffice/ExcelDownProfitLossTableList", {
            params: { year, month: queryMonth },
            responseType: "arraybuffer",
            headers: {
              Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            },
          });

          const detected = detectExcelExtOrError(res.data);

          if (detected.kind === "xlsx") {
            const monthTotalRows = await getMonthTotalRowsForExcel();
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(res.data);

            const accountSheet = workbook.worksheets[0];
            if (accountSheet) {
              setWorksheetName(workbook, accountSheet, "손익표(거래처-전체)");
            }
            addMonthTotalSheet(workbook, monthTotalRows);
            applyCalculationRulesToDownloadedWorkbook(workbook);

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            downloadBlob(blob, `${year}(손익표).xlsx`);
            return;
          }

          if (detected.kind === "xls") {
            const buffer = await convertLegacyXlsWithCalculationRules(res.data);
            downloadBlob(
              new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              }),
              `${year}(손익표).xlsx`
            );
            return;
          }

          let rows;
          try {
            rows = arrayBufferToJson(res.data);
          } catch (e) {
            Swal.fire(
              "엑셀 다운로드 실패",
              `서버가 엑셀도 아니고 JSON 파싱도 실패했습니다.\n\n(앞부분)\n${detected.preview || ""
              }`,
              "error"
            );
            return;
          }

          if (!Array.isArray(rows) || rows.length === 0) {
            Swal.fire("다운로드할 데이터가 없습니다.", "", "info");
            return;
          }

          const monthTotalRows = await getMonthTotalRowsForExcel();
          const workbook = new ExcelJS.Workbook();

          addValueRatioSheet(workbook, {
            rows,
            sheetName: "손익표(거래처-전체)",
            firstTitle: "거래처",
            firstKey: "__account_month",
            labelGetter: (r) => {
              const m = r?.month ?? r?.mm ?? r?.mon ?? "";
              const monthText = m !== "" ? `${m}월` : "";
              return `${r?.account_name ?? ""}${monthText ? ` ${monthText}` : ""}`;
            },
          });

          addMonthTotalSheet(workbook, monthTotalRows);

          const buffer = await workbook.xlsx.writeBuffer();
          const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          downloadBlob(blob, `${year}(손익표).xlsx`);
          return;
        }

        if (mode === "month") {
          const res = await excelApi.get("/HeadOffice/ExcelDownMonthProfitLossTableList", {
            params: { account_id: selectedAccountId, year, month: queryMonth },
            responseType: "arraybuffer",
            headers: {
              Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            },
          });

          const detected = detectExcelExtOrError(res.data);

          if (detected.kind === "xlsx") {
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(res.data);
            widenMonthAllCurrentColumn(workbook);
            applyCalculationRulesToDownloadedWorkbook(workbook);

            const buffer = await workbook.xlsx.writeBuffer();
            downloadBlob(
              new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              }),
              `${year}(손익표-월전체).xlsx`
            );
            return;
          }

          if (detected.kind === "xls") {
            const buffer = await convertLegacyXlsWithCalculationRules(res.data);
            downloadBlob(
              new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              }),
              `${year}(손익표-월전체).xlsx`
            );
            return;
          }

          let rows;
          try {
            rows = arrayBufferToJson(res.data);
          } catch (e) {
            Swal.fire(
              "엑셀 다운로드 실패",
              `서버가 엑셀도 아니고 JSON 파싱도 실패했습니다.\n\n(앞부분)\n${detected.preview || ""
              }`,
              "error"
            );
            return;
          }

          if (!Array.isArray(rows) || rows.length === 0) {
            Swal.fire("다운로드할 데이터가 없습니다.", "", "info");
            return;
          }

          if (!isMonthlyRecordArray(rows)) {
            Swal.fire(
              "월 전체 다운로드",
              "서버 JSON 구조가 월별 레코드(month 포함) 형태가 아니라서, 서버에서 엑셀로 내려주도록 확인이 필요합니다.\n(우선 서버 엑셀 응답을 권장)",
              "warning"
            );
            return;
          }

          const buffer = await buildMonthAllExcelLikeTemplate(rows);
          const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          downloadBlob(blob, `${year}(손익표-월전체).xlsx`);
          return;
        }

        return;
      }

      if (!editRows || editRows.length === 0) {
        Swal.fire("다운로드할 데이터가 없습니다.", "", "info");
        return;
      }

      const buffer = await buildValueRatioWorkbook({
        rows: appendCalculatedTotalRow(editRows),
        sheetName: "손익표",
        firstTitle: "월",
        firstKey: "__month",
        labelGetter: (r) => (r.__isCalculatedTotal ? "합계" : `${r.month}월`),
      });

      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      downloadBlob(blob, fileName);
    } catch (err) {
      Swal.fire("엑셀 다운로드 실패", err?.message || "오류가 발생했습니다.", "error");
    }
  };

  const handleSave = async () => {
    const modifiedRows = editRows
      .map((row) => {
        const changedFields = {};

        editableNumberFields.forEach((field) => {
          if (field === "return_cost" || field === "utility_bills") {
            const rawKey = `__raw_${field}`;
            const raw = String(row?.[rawKey] ?? "")
              .replace(/,/g, "")
              .trim();

            if (raw === "-") {
              row[field] = null;
            } else if (/^-?\d+(\.\d+)?$/.test(raw)) {
              row[field] = Number(raw);
            }
          }

          const original = Number(row._original[field] ?? 0);
          const current = Number(row[field] ?? 0);
          if (original !== current) changedFields[field] = row[field];
        });

        editableTextFields.forEach((field) => {
          const original = String(row._original[field] ?? "").trim();
          const current = String(row[field] ?? "").trim();
          if (original !== current) changedFields[field] = row[field] ?? "";
        });

        if (Object.keys(changedFields).length > 0) {
          return {
            account_id: row.account_id,
            year,
            month: row.month,
            update_id: localStorage.getItem("user_id") || "",  // ✅ 수정자 아이디
            ...changedFields,
          };
        }
        return null;
      })
      .filter((row) => row !== null);

    if (modifiedRows.length === 0) {
      Swal.fire("변경된 내용이 없습니다.", "", "info");
      return;
    }

    try {
      await api.post("/HeadOffice/ProfitLossTableSave", { rows: modifiedRows });
      Swal.fire("변경 사항이 저장되었습니다.", "", "success");
      setLockedNoteColumnWidth(null);
      setUnlockedPersonCostMonths(new Set());  // ✅ 임시 잠금해제 초기화
      fetchProfitLossTableList(queryAccountId, queryMonth, year, filterAccountIds);
    } catch (err) {
      Swal.fire("저장 실패", err.message, "error");
    }
  };

  // 인건비 등록용 엑셀 서식 파일 다운로드
  // 거래처 목록을 행으로 채운 뒤 인건비 금액을 입력할 수 있는 서식 생성
  const handlePersonCostTemplateDownload = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("인건비등록");

    // 컬럼 순서: A열 = 거래처명, B열 = 인건비 정보, C열 = ID(숨김 - 업로드 시 매핑용)
    sheet.columns = [
      { header: "거래처명", key: "account_name", width: 35 },
      { header: "인건비 정보", key: "person_cost", width: 18 },
      { header: "ID", key: "account_id", width: 22, hidden: true },
    ];

    // 헤더 행 스타일 적용
    const headerRow = sheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.fill = excelHeaderFill;
      cell.border = excelBorderThin;
    });

    // 거래처 목록을 account_id 기준 오름차순으로 정렬 후 행 추가
    (accountList || []).slice().sort((a, b) => String(a.account_id).localeCompare(String(b.account_id))).forEach((acc) => {
      const row = sheet.addRow({
        account_name: acc.account_name,
        person_cost: "",        // 사용자가 직접 입력할 인건비 금액 칸
        account_id: acc.account_id,
      });
      row.eachCell((cell) => {
        cell.border = excelBorderThin;
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    downloadBlob(blob, "인건비등록서식.xlsx");
  };

  const handlePersonCostModalOpen = async () => {
    const { isConfirmed, isDenied } = await Swal.fire({
      title: "인건비 등록",
      html: `<p style="text-align:left; margin:0; font-size:14px; line-height:2.2; color:#555;">
        ① <b>월을 선택</b>한 후 진행해주세요.<br/>
        ② <b>업로드 서식 다운로드</b>를 눌러 엑셀 서식 파일을 받습니다.<br/>
        ③ 파일을 열어 각 거래처의 <b>인건비 금액</b>을 입력합니다.<br/>
        ④ <b>업로드</b>를 눌러 작성한 파일을 등록합니다.
      </p>`,
      showDenyButton: true,
      confirmButtonText: "업로드",
      denyButtonText: "업로드 서식 다운로드",
      showCancelButton: false,
      reverseButtons: true,
      confirmButtonColor: "#4CAF50",
      denyButtonColor: "#7066e0",
    });

    if (isDenied) {
      handlePersonCostTemplateDownload();
    } else if (isConfirmed) {
      if (!validatePersonCostUploadCondition()) return;
      personCostFileInputRef.current?.click();
    }
  };

  const validatePersonCostUploadCondition = () => {
    if (!queryMonth) {
      Swal.fire("월을 선택해주세요.", "인건비 등록은 특정 월을 선택해야 합니다.", "warning");
      return false;
    }
    return true;
  };

  // 사용자가 작성한 인건비 등록 서식 엑셀 파일을 읽어 서버에 저장
  const handlePersonCostUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";   // 같은 파일 재업로드 허용을 위해 input 초기화

    setPersonCostUploading(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      const sheet = workbook.worksheets[0];
      const rows = [];
      const invalidIds = []; // 값이 비어있거나 0이어서 등록 제외할 거래처 ID

      sheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;                                    // 헤더 행 건너뜀
        const accountId = String(row.getCell(3).value ?? "").trim();  // C열: 거래처 ID
        const personCostVal = row.getCell(2).value;                    // B열: 인건비 금액
        // null, undefined, "" 모두 빈값으로 처리
        const isEmpty = personCostVal === null || personCostVal === undefined || personCostVal === "";
        const personCost = isEmpty ? null : Number(personCostVal);

        if (!accountId) return;

        // 빈값·0·숫자가 아닌 경우 미등록 목록에 추가
        if (isEmpty || !Number.isFinite(personCost) || personCost === 0) {
          invalidIds.push(accountId);
          return;
        }

        rows.push({
          account_id: accountId,
          year,
          month: queryMonth,
          person_cost: personCost,
          update_id: localStorage.getItem("user_id") || "",
        });
      });

      // account_id → account_name 매핑
      const nameMap = {};
      (accountList || []).forEach((acc) => { nameMap[String(acc.account_id)] = acc.account_name; });

      const toNameList = (ids) =>
        ids.map((id) => nameMap[String(id)] || id).join("<br/>");

      if (rows.length === 0 && invalidIds.length === 0) {
        Swal.fire("업로드할 데이터가 없습니다.", "인건비 금액이 입력된 행이 없습니다.", "info");
        return;
      }

      let savedIds = [];
      let skippedIds = [];

      // 유효한 행이 있을 때만 서버에 저장 요청
      if (rows.length > 0) {
        const result = await savePersonCostExcel(rows);
        savedIds = result.saved || [];
        skippedIds = result.skipped || [];
      }

      const totalCount = savedIds.length + skippedIds.length + invalidIds.length;
      const sectionStyle = "text-align:left; margin-bottom:12px;";
      const listStyle = "font-size:13px; color:#555; margin-top:4px; line-height:1.8;";

      let html = `<div style="text-align:left; margin-bottom:10px; font-size:13px; color:#888;">총 ${totalCount}건 처리 &nbsp;|&nbsp; 등록: ${savedIds.length}건 &nbsp;|&nbsp; 미등록: ${skippedIds.length + invalidIds.length}건</div>`;
      html += "<div style='max-height:300px; overflow-y:auto; padding-right:4px;'>";
      if (savedIds.length > 0) {
        html += `<div style="${sectionStyle}"><b style="color:#4CAF50;">✔ 등록 완료 (${savedIds.length}건)</b><div style="${listStyle}">${toNameList(savedIds)}</div></div>`;
      }
      if (skippedIds.length > 0) {
        html += `<div style="${sectionStyle}"><b style="color:#e67e22;">⚠ 기존 데이터 존재 - 미등록 (${skippedIds.length}건)</b><div style="${listStyle}">${toNameList(skippedIds)}</div></div>`;
      }
      if (invalidIds.length > 0) {
        html += `<div style="${sectionStyle}"><b style="color:#aaa;">— 값 없음 - 미등록 (${invalidIds.length}건)</b><div style="${listStyle}">${toNameList(invalidIds)}</div></div>`;
      }
      html += "</div>";

      Swal.fire({
        title: "인건비 등록 결과",
        html,
        icon: savedIds.length > 0 ? "success" : "info",
      });
      if (savedIds.length > 0) fetchProfitLossTableList(queryAccountId, queryMonth, year, filterAccountIds);
    } catch (err) {
      Swal.fire("업로드 실패", err?.message || "오류가 발생했습니다.", "error");
    } finally {
      setPersonCostUploading(false);
    }
  };

  const handleInputChange = (rowIdx, field, value) => {
    const newRows = [...editRows];

    if (editableTextFields.includes(field)) {
      newRows[rowIdx][field] = value;
      setEditRows(newRows);
      return;
    }

    const rawKey = `__raw_${field}`;
    const raw = String(value ?? "");
    const cleaned = raw.replace(/,/g, "").trim();

    if (cleaned === "") {
      newRows[rowIdx][field] = null;
      newRows[rowIdx][rawKey] = "";
      setEditRows(newRows);
      return;
    }

    if (field === "return_cost" || field === "utility_bills") {
      if (cleaned === "-") {
        newRows[rowIdx][field] = null;
        newRows[rowIdx][rawKey] = "-";
        setEditRows(newRows);
        return;
      }

      const isNegative = cleaned.includes("-");
      const digits = cleaned.replace(/-/g, "");

      if (!/^\d+(\.\d+)?$/.test(digits)) {
        newRows[rowIdx][rawKey] = raw;
        setEditRows(newRows);
        return;
      }

      const n = Number(digits);
      newRows[rowIdx][field] = isNegative ? -n : n;
      newRows[rowIdx][rawKey] = "";
      setEditRows(newRows);
      return;
    }

    const isValidNumber = /^\d+(\.\d+)?$/.test(cleaned);
    if (!isValidNumber) {
      setEditRows(newRows);
      return;
    }

    newRows[rowIdx][field] = Number(cleaned);
    setEditRows(newRows);
  };

  if (loading) return <LoadingScreen />;

  return (
    <>
      <MDBox
        sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap", pt: 0.5, pb: 1, pr: 1 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <RadioGroup
            row
            value={radioFilter}
            onChange={handleRadioChange}
            sx={{ flexWrap: "nowrap" }}
          >
            {["전체", "요양원", "산업체", "도소매"].map((label) => (
              <FormControlLabel
                key={label}
                value={label}
                control={<Radio size="small" />}
                label={label}
                sx={{ mr: 0.5, "& .MuiFormControlLabel-label": { fontSize: "13px", position: "relative", top: "-1px", left: "-9px" } }}
              />
            ))}
          </RadioGroup>
          <Autocomplete
            size="small"
            sx={{ minWidth: 200 }}
            options={filteredAccountOptions}
            value={selectedAccount}
            onChange={(_, newValue) => {
              // 입력 비움 시 거래처 선택 유지
              if (!newValue) return;
              setSelectedAccountId(newValue.account_id);
            }}
            inputValue={accountInput}
            onInputChange={(_, newValue) => setAccountInput(newValue)}
            getOptionLabel={(option) => option?.account_name ?? ""}
            isOptionEqualToValue={(option, value) =>
              String(option.account_id) === String(value.account_id)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="거래처 검색"
                placeholder="거래처명을 입력"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    selectAccountByInput();
                  }
                }}
              />
            )}
          />
        </Box>

        <Select value={year} onChange={(e) => setYear(Number(e.target.value))} size="small">
          {Array.from({ length: 10 }, (_, i) => today.year() - 5 + i).map((y) => (
            <MenuItem key={y} value={y}>
              {y}년
            </MenuItem>
          ))}
        </Select>

        <Select value={month} onChange={(e) => setMonth(e.target.value)} size="small">
          <MenuItem value="ALL">전체</MenuItem>
          {Array.from({ length: 12 }, (_, i) => String(i + 1)).map((m) => (
            <MenuItem key={m} value={m}>
              {m}월
            </MenuItem>
          ))}
        </Select>

        <MDButton
          variant="contained"
          color="info"
          size="small"
          onClick={handleRefresh}
          startIcon={<RefreshIcon />}
        >
          새로고침
        </MDButton>

        <MDButton
          variant="contained"
          color="warning"
          size="small"
          disabled={personCostUploading}
          onClick={handlePersonCostModalOpen}
        >
          {personCostUploading ? "업로드 중..." : "인건비 등록"}
        </MDButton>

        <MDButton
          variant="contained"
          color="success"
          size="small"
          onClick={handleExcelDownload}
          startIcon={<DownloadIcon />}
        >
          엑셀다운로드
        </MDButton>

        <MDButton variant="contained" color="info" size="small" onClick={handleSave}>
          저장
        </MDButton>
      </MDBox>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            ref={tableBoxRef}
            sx={{
              maxHeight: "75vh",
              overflowX: "auto",
              overflowY: "auto",
              position: "relative",
              isolation: "isolate",
              zIndex: 0,
              WebkitOverflowScrolling: "touch",
              "& table": {
                borderCollapse: "collapse",
                width: "100%",
                minWidth: "1800px",
                borderSpacing: 0,
                borderCollapse: "separate",
                fontWeight: "bold",
              },
              "& th, & td": {
                border: "1px solid #686D76",
                textAlign: "center",
                fontSize: "12px",
                padding: "4px",
                borderRight: "1px solid #686D76",
                borderLeft: "1px solid #686D76",
              },
              "& th": {
                backgroundColor: "#f0f0f0",
              },
              "& thead tr:first-of-type th": {
                position: "sticky",
                top: 0,
                zIndex: 4,
              },
              "& thead tr:nth-of-type(2) th": {
                position: "sticky",
                top: `${secondRowTop}px`,
                zIndex: 4,
              },
              ".sticky-col": {
                position: "sticky",
                left: 0,
                background: "#e8f0ff",
                zIndex: 3,
                borderRight: "1px solid #686D76",
                width: "50px",
                minWidth: "50px",
                maxWidth: "50px",
              },
              ".sticky-header": {
                background: "#e8f0ff",
                borderRight: "1px solid #686D76",
              },
              "& thead tr:first-of-type th.sticky-col": {
                zIndex: 6,
              },
              // 모바일 가로 모드에서는 테이블 레이어를 낮추고 고정 컬럼을 해제해 탭 헤더를 우선 노출
              "@media (orientation: landscape) and (max-height: 900px)": {
                zIndex: 0,
                "& table, & thead, & tbody, & tr, & th, & td, & input": {
                  zIndex: "0 !important",
                },
                ".sticky-col": {
                  position: "static",
                  left: "auto",
                  zIndex: "0 !important",
                },
                ".sticky-header": {
                  zIndex: "0 !important",
                },
              },
            }}
          >
            <table>
              <thead ref={theadRef}>
                <tr>
                  <th className="sticky-col sticky-header" rowSpan={2} style={{ zIndex: 7 }}>
                    월
                  </th>
                  {filteredHeaders.flatMap((h) =>
                    h.group
                      ? [
                        <th key={h.group} colSpan={h.cols.length}>
                          {h.group}
                        </th>,
                      ]
                      : h.cols.map((c) => (
                        <th key={c} rowSpan={2} data-field={fieldMap[c]?.value ?? ""}>
                          {c === "매출소계(판장금제외)" ? (
                            <>
                              매출소계
                              <br />
                              (판장금제외)
                            </>
                          ) : (
                            c
                          )}
                        </th>
                      ))
                  )}
                </tr>
                <tr>
                  {filteredHeaders
                    .filter((h) => h.group)
                    .flatMap((h) =>
                      h.cols.map((c) => (
                        <th
                          key={c}
                          data-field={fieldMap[c]?.value ?? ""}
                        >
                          {c === "매출소계(판장금제외)" ? (
                            <>
                              매출소계
                              <br />
                              (판장금제외)
                            </>
                          ) : c === "반환금" ? (
                            <>
                              반환금
                              <br />
                              (학교유형)
                            </>
                          ) : (
                            c
                          )}
                        </th>
                      ))
                    )}
                </tr>
              </thead>

              <tbody>
                {totalRow && editRows.length > 0 && (
                  <React.Fragment key="total">
                    <tr style={{ background: "#fff9e6" }}>
                      <td
                        className="sticky-col"
                        rowSpan={2}
                        style={{ fontWeight: "bold", background: "#fff3cc" }}
                      >
                        합계
                      </td>
                      {filteredHeaders.flatMap((h) =>
                        h.cols.map((col) => {
                          const field = fieldMap[col]?.value;
                          const isNote = field === noteField;
                          return (
                            <td key={col} style={isNote ? { textAlign: "left" } : {}}>
                              <input
                                type="text"
                                value={isNote ? "" : formatNumber(totalRow[field] ?? 0)}
                                disabled
                                style={{
                                  width: "90px",
                                  height: "20px",
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                  textAlign: "right",
                                  border: "none",
                                  background: "transparent",
                                  WebkitTextFillColor: "inherit",
                                  opacity: 1,
                                }}
                              />
                            </td>
                          );
                        })
                      )}
                    </tr>
                    <tr style={{ background: "#fff9e6" }}>
                      {filteredHeaders.flatMap((h) =>
                        h.cols.map((col) => {
                          const ratioField = fieldMap[col]?.ratio;
                          return (
                            <td
                              key={`total_${col}_ratio`}
                              style={{ fontSize: "11px", color: "#CD2C58" }}
                            >
                              {formatRatioCell(totalRow, ratioField)}
                            </td>
                          );
                        })
                      )}
                    </tr>
                  </React.Fragment>
                )}
                {editRows.map((r, i) => (
                  <React.Fragment key={i}>
                    <tr>
                      <td
                        className="sticky-col"
                        rowSpan={2}
                        style={{ fontWeight: "bold", background: "#fafafa" }}
                      >
                        {r.month}월
                      </td>

                      {filteredHeaders.flatMap((h) =>
                        h.cols.map((col) => {
                          const field = fieldMap[col]?.value;

                          const isAllAccount = selectedAccountId === "ALL";  // ✅ 전체 조회 시 편집 불가
                          const isText = !isAllAccount && editableTextFields.includes(field);
                          const isNumber = !isAllAccount && editableNumberFields.includes(field) && !(field === "person_cost" && isPersonCostLocked(r)) && !(field === "person_cost" && !hasPersonCostEditPermission);  // ✅ 지난 달 인건비 잠금 + 권한 없으면 편집 불가
                          const isNote = field === noteField;

                          const isChanged = (() => {
                            if (isText) {
                              const o = String(r._original[field] ?? "").trim();
                              const c = String(r[field] ?? "").trim();
                              return o !== c;
                            }
                            if (isNumber) {
                              const o = Number(r._original[field] ?? 0);
                              const c = Number(r[field] ?? 0);
                              return o !== c;
                            }
                            return false;
                          })();

                          return (
                            <td
                              key={col}
                              data-field={field}
                              onContextMenu={field === "person_cost" && isPersonCostLocked(r) ? (e) => handlePersonCostContextMenu(e, r) : undefined}
                              style={{
                                ...(isNote && lockedNoteColumnWidth
                                  ? {
                                    width: `${lockedNoteColumnWidth}px`,
                                    minWidth: `${lockedNoteColumnWidth}px`,
                                    maxWidth: `${lockedNoteColumnWidth}px`,
                                  }
                                  : {}),
                                ...(isNote ? { textAlign: "left" } : {}),
                                ...(field === "person_cost" && isPersonCostLocked(r) ? { cursor: "context-menu" } : {}),
                              }}
                            >
                              {isText ? (
                                <input
                                  type="text"
                                  value={r[field] ?? ""}
                                  style={{
                                    width: isNote
                                      ? lockedNoteColumnWidth
                                        ? "100%"
                                        : getNoteInputWidth(r[field] ?? "")
                                      : "80px",
                                    height: "20px",
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                    textAlign: isNote ? "left" : "right",
                                    border: "none",
                                    background: "transparent",
                                    color: isChanged ? "red" : "black",
                                    boxSizing: "border-box",
                                  }}
                                  onFocus={() => {
                                    if (isNote) lockNoteColumn();
                                  }}
                                  onChange={(e) => handleInputChange(i, field, e.target.value)}
                                />
                              ) : isNumber ? (
                                (() => {
                                  const rawKey = `__raw_${field}`;
                                  const displayValue =
                                    (field === "return_cost" || field === "utility_bills") && r?.[rawKey]
                                      ? r[rawKey]
                                      : r[field] !== null && r[field] !== undefined
                                        ? formatNumber(r[field])
                                        : "";

                                  return (
                                    <input
                                      type="text"
                                      value={displayValue}
                                      style={{
                                        width: "110px",
                                        height: "20px",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                        textAlign: "right",
                                        border: "none",
                                        background: "transparent",
                                        color: isChanged ? "red" : "black",
                                      }}
                                      onChange={(e) => handleInputChange(i, field, e.target.value)}
                                    />
                                  );
                                })()
                              ) : (
                                <input
                                  type="text"
                                  value={editableTextFields.includes(field) ? (isAllAccount ? "" : (getDisplayValue(r, field) ?? "")) : formatNumber(getDisplayValue(r, field) ?? 0)}
                                  disabled
                                  style={{
                                    width: "90px",
                                    height: "20px",
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                    textAlign: "right",
                                    border: "none",
                                    background: "transparent",
                                    WebkitTextFillColor: "inherit",
                                    opacity: 1,
                                  }}
                                />
                              )}
                            </td>
                          );
                        })
                      )}
                    </tr>

                    <tr>
                      {filteredHeaders.flatMap((h) =>
                        h.cols.map((col) => {
                          const ratioField = fieldMap[col]?.ratio;
                          const isNote = fieldMap[col]?.value === noteField;
                          return (
                            <td
                              key={`${col}_ratio`}
                              style={{
                                fontSize: "11px",
                                color: "#CD2C58",
                                ...(isNote ? { textAlign: "left" } : {}),
                              }}
                            >
                              {formatRatioCell(r, ratioField)}
                            </td>
                          );
                        })
                      )}
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </Box>
        </Grid>
      </Grid>

      <input
        type="file"
        accept=".xlsx,.xls"
        ref={personCostFileInputRef}
        style={{ display: "none" }}
        onChange={handlePersonCostUpload}
      />

      {ctxMenu.open && (
        <div
          onClick={closeCtxMenu}
          onContextMenu={(e) => { e.preventDefault(); closeCtxMenu(); }}
          style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 10000 }}
        >
          <div
            style={{
              position: "absolute",
              top: ctxMenu.mouseY,
              left: ctxMenu.mouseX,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 8,
              boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
              minWidth: 140,
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={{ width: "100%", padding: "10px 12px", border: "none", background: "transparent", textAlign: "left", cursor: "pointer", fontSize: 13 }}
              onClick={() => {
                setUnlockedPersonCostMonths((prev) => new Set([...prev, ctxMenu.month]));
                closeCtxMenu();
              }}
            >
              ✏️ 인건비 수정
            </button>
          </div>
        </div>
      )}
    </>
  );
}
